import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, CheckCircle, Loader2, Mail, Building2, User, Briefcase } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface AccessRequestFormProps {
  onSuccess?: () => void;
}

export const AccessRequestForm: React.FC<AccessRequestFormProps> = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    jobTitle: '',
    businessNeed: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.company.trim()) {
      newErrors.company = 'Company name is required';
    }

    if (!formData.jobTitle.trim()) {
      newErrors.jobTitle = 'Job title is required';
    }

    if (!formData.businessNeed.trim()) {
      newErrors.businessNeed = 'Business need description is required';
    } else if (formData.businessNeed.trim().length < 50) {
      newErrors.businessNeed = 'Please provide at least 50 characters explaining your business need';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Call our access request edge function
      const { data, error } = await supabase.functions.invoke('access-requests', {
        body: {
          action: 'submit',
          ...formData
        }
      });

      if (error) {
        throw error;
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to submit access request');
      }

      setSubmitted(true);
      toast.success('Access request submitted successfully!');
      onSuccess?.();

    } catch (error: any) {
      console.error('Access request error:', error);
      
      if (error.message?.includes('rate limit')) {
        toast.error('Too many requests. Please try again later.');
      } else if (error.message?.includes('already exists')) {
        toast.error('An access request with this email already exists.');
      } else {
        toast.error('Failed to submit access request. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  if (submitted) {
    return (
      <Card className="shadow-card border-border/50">
        <CardContent className="pt-6">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 gradient-primary rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="h-8 w-8 text-white" />
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-semibold">Request Submitted Successfully!</h3>
              <p className="text-muted-foreground">
                Thank you for your interest in BillPortal. Our team will review your request 
                and contact you within 2-3 business days.
              </p>
            </div>
            <div className="bg-success/10 p-4 rounded-lg border border-success/20">
              <p className="text-success text-sm">
                We'll send updates to <strong>{formData.email}</strong>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-card border-border/50">
      <CardHeader className="text-center">
        <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
          <Mail className="h-6 w-6 text-primary" />
        </div>
        <CardTitle className="text-xl font-semibold">Request Access to BillPortal</CardTitle>
        <p className="text-muted-foreground">
          Fill out this form and our team will review your request within 2-3 business days.
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Full Name */}
          <div className="space-y-2">
            <Label htmlFor="fullName" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Full Name *
            </Label>
            <Input
              id="fullName"
              type="text"
              placeholder="John Doe"
              value={formData.fullName}
              onChange={(e) => handleInputChange('fullName', e.target.value)}
              className={errors.fullName ? 'border-destructive' : ''}
              disabled={loading}
            />
            {errors.fullName && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.fullName}
              </p>
            )}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Email Address *
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="john@company.com"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={errors.email ? 'border-destructive' : ''}
              disabled={loading}
            />
            {errors.email && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.email}
              </p>
            )}
          </div>

          {/* Company */}
          <div className="space-y-2">
            <Label htmlFor="company" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Company Name *
            </Label>
            <Input
              id="company"
              type="text"
              placeholder="Acme Corporation"
              value={formData.company}
              onChange={(e) => handleInputChange('company', e.target.value)}
              className={errors.company ? 'border-destructive' : ''}
              disabled={loading}
            />
            {errors.company && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.company}
              </p>
            )}
          </div>

          {/* Job Title */}
          <div className="space-y-2">
            <Label htmlFor="jobTitle" className="flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              Job Title *
            </Label>
            <Input
              id="jobTitle"
              type="text"
              placeholder="Finance Manager"
              value={formData.jobTitle}
              onChange={(e) => handleInputChange('jobTitle', e.target.value)}
              className={errors.jobTitle ? 'border-destructive' : ''}
              disabled={loading}
            />
            {errors.jobTitle && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.jobTitle}
              </p>
            )}
          </div>

          {/* Business Need */}
          <div className="space-y-2">
            <Label htmlFor="businessNeed" className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Business Need *
            </Label>
            <Textarea
              id="businessNeed"
              placeholder="Please describe how BillPortal would help your organization with bill processing, document management, or workflow automation. Include details about your current challenges and expected benefits."
              value={formData.businessNeed}
              onChange={(e) => handleInputChange('businessNeed', e.target.value)}
              className={`min-h-24 resize-none ${errors.businessNeed ? 'border-destructive' : ''}`}
              disabled={loading}
            />
            <div className="flex justify-between items-center">
              {errors.businessNeed ? (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.businessNeed}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Minimum 50 characters required
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                {formData.businessNeed.length}/500
              </p>
            </div>
          </div>

          {/* Submit Button */}
          <Button 
            type="submit" 
            className="w-full" 
            disabled={loading}
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Submit Access Request
          </Button>

          {/* Back to Login Button */}
          <Button 
            type="button"
            variant="outline" 
            className="w-full" 
            onClick={() => window.location.href = '/auth'}
          >
            Back to Login
          </Button>

          {/* Info Message */}
          <div className="bg-primary/10 p-4 rounded-lg border border-primary/20">
            <p className="text-primary text-sm">
              <AlertCircle className="h-4 w-4 inline mr-2" />
              <strong>What happens next:</strong> Our team will review your request and contact you 
              within 2-3 business days. If approved, you'll receive an invitation to join BillPortal.
            </p>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};